import React, { useState } from 'react';
import { IoSend, IoSparkles } from 'react-icons/io5';
import './ActivityDescriptionBox.css';

interface ActivityDescriptionBoxProps {
  onAnalysisComplete: () => void;
  onAnalyze: (description: string) => Promise<void>;
  isAnalyzing?: boolean;
}

const ActivityDescriptionBox: React.FC<ActivityDescriptionBoxProps> = ({
  onAnalysisComplete,
  onAnalyze,
  isAnalyzing = false
}) => {
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('ActivityDescriptionBox: handleSubmit called');
    
    if (!description.trim()) {
      setError('Please describe your activity');
      return;
    }

    if (description.trim().length < 10) {
      setError('Please provide more details about your activity');
      return;
    }

    setError('');
    
    try {
      console.log('ActivityDescriptionBox: calling onAnalyze');
      await onAnalyze(description.trim());
      console.log('ActivityDescriptionBox: onAnalyze completed, calling onAnalysisComplete');
      onAnalysisComplete();
      console.log('ActivityDescriptionBox: onAnalysisComplete called');
    } catch (error) {
      console.error('ActivityDescriptionBox: Analysis failed:', error);
      setError('Failed to analyze activity. Please try again.');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
    if (error) setError('');
  };

  return (
    <div className="activity-description-box">
      <div className="activity-description-header">
        <div className="activity-description-icon">
          <IoSparkles className="text-purple-600" />
        </div>
        <div>
          <h4 className="activity-description-title">Describe Your Activity</h4>
          <p className="activity-description-subtitle">
            Tell us about an extracurricular activity, and our AI will help organize the details for your college applications.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="activity-description-form">
        <div className="activity-description-input-container">
          <textarea
            value={description}
            onChange={handleInputChange}
            placeholder="Example: I was captain of the varsity soccer team for two years. I led the team to win the state championship in my senior year and organized weekly community service events where we coached younger kids. I practiced 15 hours per week during the season and plan to play intramural soccer in college..."
            className={`activity-description-textarea ${error ? 'activity-description-textarea-error' : ''}`}
            rows={6}
            maxLength={1000}
            disabled={isAnalyzing}
          />
          <div className="activity-description-char-count">
            {description.length}/1000 characters
          </div>
        </div>

        {error && (
          <div className="activity-description-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isAnalyzing || !description.trim()}
          className="activity-description-submit"
        >
          {isAnalyzing ? (
            <>
              <div className="activity-description-spinner" />
              Analyzing Activity...
            </>
          ) : (
            <>
              <IoSend className="w-4 h-4" />
              Analyze Activity
            </>
          )}
        </button>
      </form>

      <div className="activity-description-tips">
        <h5 className="activity-description-tips-title">💡 Tips for better results:</h5>
        <ul className="activity-description-tips-list">
          <li>Include your role or position (captain, president, volunteer, etc.)</li>
          <li>Mention the organization or group name</li>
          <li>Describe what you accomplished or any recognition received</li>
          <li>Include time commitment (hours per week, which years)</li>
          <li>Note if you plan to continue this activity in college</li>
        </ul>
      </div>
    </div>
  );
};

export default ActivityDescriptionBox;